/**
 * HRVTrendChart component - HRV trend visualization with baseline overlay.
 *
 * Displays HRV values over time with:
 * - Daily HRV measurements
 * - 7-day rolling average
 * - Baseline reference line
 * - Status zones (Balanced/Low/High)
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine
} from 'recharts';
import { HRVTrendData } from '../../types/progress';

interface HRVTrendChartProps {
  data: HRVTrendData;
}

const HRVTrendChart: React.FC<HRVTrendChartProps> = ({ data }) => {
  // Format data for Recharts
  const chartData = data.data_points.map(point => ({
    date: new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    hrv: point.hrv_value,
    avg: point.seven_day_avg,
    status: point.status
  }));

  // Calculate baseline zones
  const baseline = data.baseline || 0;
  const balancedLow = baseline * 0.85;
  const balancedHigh = baseline * 1.15;
  const maxHrv = Math.max(...data.data_points.map(p => p.hrv_value || 0));

  return (
    <div className="card shadow-elegant animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-2xl font-serif font-bold text-neutral-900 mb-2">
            💓 Heart Rate Variability Trend
          </h3>
          <p className="text-neutral-600">
            Track your autonomic nervous system recovery and resilience
          </p>
        </div>
        {data.baseline && (
          <div className="text-right">
            <div className="text-sm text-neutral-600 mb-1">Your Baseline</div>
            <div className="text-2xl font-bold text-primary-700">{data.baseline.toFixed(1)}</div>
            <div className="text-xs text-neutral-500">ms</div>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-4 rounded-xl border border-primary-200">
          <div className="text-xs text-neutral-600 mb-1">Average</div>
          <div className="text-2xl font-bold text-primary-700">{data.statistics.avg}</div>
          <div className="text-xs text-neutral-500 mt-1">ms</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
          <div className="text-xs text-neutral-600 mb-1">Minimum</div>
          <div className="text-2xl font-bold text-blue-700">{data.statistics.min}</div>
          <div className="text-xs text-neutral-500 mt-1">ms</div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
          <div className="text-xs text-neutral-600 mb-1">Maximum</div>
          <div className="text-2xl font-bold text-green-700">{data.statistics.max}</div>
          <div className="text-xs text-neutral-500 mt-1">ms</div>
        </div>
        <div className="bg-gradient-to-br from-accent-50 to-accent-100 p-4 rounded-xl border border-accent-200">
          <div className="text-xs text-neutral-600 mb-1">Trend</div>
          <div className="text-lg font-bold text-accent-700 capitalize">{data.statistics.trend}</div>
          <div className="text-xs text-neutral-500 mt-1">
            {data.statistics.trend === 'improving' && '📈'}
            {data.statistics.trend === 'declining' && '📉'}
            {data.statistics.trend === 'stable' && '➡️'}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-gradient-to-br from-neutral-50 to-white p-6 rounded-xl border border-neutral-200">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />

            {/* Status zones (if baseline exists) */}
            {data.baseline && (
              <>
                <ReferenceArea
                  y1={balancedLow}
                  y2={balancedHigh}
                  fill="#16a34a"
                  fillOpacity={0.1}
                  label={{ value: 'Balanced Zone', position: 'insideTopRight', fill: '#166534', fontSize: 12 }}
                />
                <ReferenceArea
                  y1={0}
                  y2={balancedLow}
                  fill="#dc2626"
                  fillOpacity={0.1}
                />
                <ReferenceArea
                  y1={balancedHigh}
                  y2={maxHrv + 10}
                  fill="#f59e0b"
                  fillOpacity={0.1}
                />
                <ReferenceLine
                  y={data.baseline}
                  stroke="#6b7280"
                  strokeDasharray="5 5"
                  label={{ value: 'Baseline', position: 'right', fill: '#6b7280', fontSize: 11 }}
                />
              </>
            )}

            <XAxis
              dataKey="date"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#6b7280' }}
              label={{ value: 'HRV (ms)', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '2px solid #C98CCC',
                borderRadius: '0.75rem',
                padding: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}
              labelStyle={{ color: '#1A1A1A', fontWeight: 600, marginBottom: '4px' }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />

            <Line
              type="monotone"
              dataKey="hrv"
              stroke="#C98CCC"
              strokeWidth={2}
              dot={{ r: 3, fill: '#C98CCC' }}
              name="Daily HRV"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="avg"
              stroke="#8B7355"
              strokeWidth={3}
              dot={false}
              name="7-Day Average"
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Data Completeness */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-neutral-600">
          Data completeness: <span className="font-semibold text-neutral-900">{data.data_completeness.toFixed(1)}%</span>
        </div>
        <div className="text-neutral-500">
          {data.data_points.length} days with HRV data
        </div>
      </div>
    </div>
  );
};

export default HRVTrendChart;
